// Libraries
import React, { useRef, useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faFilm,
  faVideo,
  faTrashCan,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Tooltip from '@mui/material/Tooltip';

// Components
import GeneralModal from '../components/Modals/GeneralModal.js';
import ProFlag from '../components/Elements/ProFlag.js';
import Icon from '../components/Elements/Icon.js';
import CollaboratorDropdown from '../components/Fragments/Project/collaboratorDropdown.js';
import CollaboratorBubbleList from '../components/Fragments/Project/collaboratorBubbleList.js';

// Services
import ScriptService from '../services/scriptService';

// Context
import { UserContext } from '../context/User';
import SessionContext from '../context/Session';

// Construct Component
const ScriptList = ({ script, updateScript, onDeleteScript, refreshScripts }) => {
  const { user } = useContext(UserContext);
  const { isMobile, generalModalDetails, setGeneralModalDetails } = useContext(SessionContext);

  const [showCollabDropdown, setShowCollabDropdown] = useState(false);

  // Title truncation detection
  const titleRef = useRef(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const truncated = el.scrollWidth > el.clientWidth;
    if (truncated !== isTitleTruncated) {
      setIsTitleTruncated(truncated);
    }
  }, [script?.script_name, isMobile, isTitleTruncated]);

  const formatCreatedAt = (createdAt) => {
    const date = new Date(createdAt);

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    const options = { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = date.toLocaleString('en-US', options);

    return `${month}/${day}/${year} at ${time}`;
  };

  const getScriptFormatLabel = () => (script.script_path ? 'PDF Script' : 'Copy/Paste Script');

  // Preserve your existing "Auto generate" rules, but display as a pill like the new design
  const getAutoGenerateLabel = () => {
    if (!script || !script.features) return 'Off';

    const f = script.features.toLowerCase();
    if (f === 'pro' || f === 'pro freemium') return 'On';
    if (f === 'pro freemium10') return '10 Free Scenes';
    if (f === 'pro freemium5') return '5 Free Scenes';
    if (f === 'basicplus') return '5 Free Scenes';
    return 'Off';
  };

  const autoGenerateLabel = getAutoGenerateLabel();
  const autoGenerateIsOn = autoGenerateLabel === 'On';
  const scriptFormatLabel = getScriptFormatLabel();

  // Determine if this script level is one of the "free scenes" tiers
  const featuresKey = script?.features?.toLowerCase() || '';
  const isFreeScenesTier =
    featuresKey === 'pro freemium10' ||
    featuresKey === 'pro freemium5' ||
    featuresKey === 'basicplus';

  // Only show ProFlag when not Pro (same logic as before)
  const showProFlag = !script?.features || script.features.toLowerCase() !== 'pro';

  // Only overlay ProFlag when auto-generate is Off (per your request)
  const showProOverlay = showProFlag && !autoGenerateIsOn;

  const removeMyAccess = async (scriptId) => {
    let collab = script.collaborators.editors.find((c) => c.user_id === user.id);
    if (!collab) collab = script.collaborators.viewers.find((c) => c.user_id === user.id);

    if (!collab) {
      console.error('Could not find user in collaborators list');
      return;
    }

    const removed = await ScriptService.editCollaborator(scriptId, collab.id, 'removeAccess');
    if (removed) onDeleteScript(scriptId);

    setGeneralModalDetails({ open: false, header: null, message: null });
  };

  async function confirmedDeleteScript(scriptId) {
    try {
      setGeneralModalDetails({ open: false, header: null, message: null });
      const deleted = await ScriptService.deleteScript(scriptId);

      if (deleted) {
        onDeleteScript(scriptId);
        toast.success('Your project has been deleted');
      } else {
        toast.error('We were unable to delete this project. Please try again.');
      }
    } catch (error) {
      toast.error('We were unable to delete this project.');
    }
  }

  const callRemoveMeModal = (scriptId) => {
    setGeneralModalDetails({
      open: true,
      header: (
        <span>
          <FontAwesomeIcon icon={faTrashCan} className="scriptListModalIcon" />
          Remove yourself as a collaborator for this project
        </span>
      ),
      message: (
        <div>
          You cannot delete the project for other users, but you can remove yourself as a
          collaborator which will immediately revoke your access and remove it from your dashboard.
          <br />
          <br />
          We will also let the owner know that you are no longer collaborating on this project.
          Would you like to remove yourself from this project?
          <br />
          <br />
          <div className="fullWidthContainer">
            <button
              className="buttonGray OnePortionFlex"
              onClick={() => setGeneralModalDetails({ open: false, header: null, message: null })}
            >
              Cancel
            </button>
            <button
              className="OnePortionFlex scriptListDangerBtn"
              onClick={() => removeMyAccess(scriptId)}
            >
              Remove me
            </button>
          </div>
        </div>
      ),
    });
  };

  const deleteScriptModal = (scriptId, scriptName) => {
    if (script.level !== 'owner') {
      callRemoveMeModal(scriptId);
      return;
    }

    setGeneralModalDetails({
      open: true,
      header: (
        <span>
          <FontAwesomeIcon icon={faTrashCan} className="scriptListModalIcon" />
          Delete Script
        </span>
      ),
      message: (
        <div>
          Are you sure you want to delete {scriptName}? This action cannot be undone and all scenes
          you shotlisted will be lost.
          <br />
          <br />
          Deleting this project will not reset your credits. If you need more credits, please{' '}
          <a href="https://www.shotkraft.com/contact-us" className="linkText linkTextNoPadding">
            contact us
          </a>
          .
          <br />
          <br />
          <div className="fullWidthContainer">
            <button
              className="buttonGray OnePortionFlex"
              onClick={() => setGeneralModalDetails({ open: false, header: null, message: null })}
            >
              Cancel
            </button>
            <button
              className="OnePortionFlex scriptListDangerBtn"
              onClick={() => confirmedDeleteScript(scriptId)}
            >
              Delete Script
            </button>
          </div>
        </div>
      ),
    });
  };

  const goToScript = (scriptId) => {
    window.location.href = `/app/ShotList/${scriptId}`;
  };

  const isOwner = script.level === 'owner';

  const titleClassNames = ['scriptListTitle', isTitleTruncated ? 'scriptListTitleTruncated' : '']
    .filter(Boolean)
    .join(' ');

  const ownerPillClassNames = [
    'scriptListOwnerPill',
    !isOwner ? 'scriptListOwnerPillCollaborator' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {generalModalDetails && (
        <GeneralModal
          generalModalDetails={generalModalDetails}
          setModalIsOpen={setGeneralModalDetails}
        />
      )}

      {/* Card itself; parent grid controls column layout */}
      <div
        className={[
          'projectCard',
          'scriptListCard',
          'scriptListCardGridItem',
          isMobile ? 'scriptListCardMobile' : 'scriptListCardDesktop',
        ].join(' ')}
      >
        {/* Header */}
        <div className="scriptListHeaderRow" onClick={() => goToScript(script.id)}>
          <div className="scriptListHeaderLeft">
            <Tooltip
              title={isTitleTruncated ? script.script_name : ''}
              placement="top"
              arrow
              disableHoverListener={!isTitleTruncated}
              disableFocusListener={!isTitleTruncated}
              disableTouchListener={!isTitleTruncated}
            >
              <h2 ref={titleRef} className={titleClassNames}>
                {script.script_name}
              </h2>
            </Tooltip>

            <div className="scriptListCreated">Created {formatCreatedAt(script.created_at)}</div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToScript(script.id);
            }}
            className="scriptListViewBtn"
          >
            View Project
          </button>
        </div>

        {/* Main clickable content */}
        <div className="scriptListMainContent" onClick={() => goToScript(script.id)}>
          {/* Stat tiles */}
          <div className="scriptListStatGrid">
            <div className="scriptListStatCard">
              <div className="scriptListStatHeader">
                <FontAwesomeIcon icon={faFileLines} />
                <span>Pages</span>
              </div>
              <div className="scriptListStatBody">
                <div className="scriptListStatValue">{script.page_count || 0}</div>
              </div>
            </div>

            <div className="scriptListStatCard">
              <div className="scriptListStatHeader">
                <FontAwesomeIcon icon={faFilm} />
                <span>Scenes</span>
              </div>
              <div className="scriptListStatBody">
                <div className="scriptListStatValue">{script.scene_count || 0}</div>
              </div>
            </div>

            <div className="scriptListStatCard">
              <div className="scriptListStatHeader">
                <FontAwesomeIcon icon={faVideo} />
                <span>Shots</span>
              </div>
              <div className="scriptListStatBody">
                <div className="scriptListStatValue">{script.shot_count || 0}</div>
              </div>
            </div>
          </div>

          {/* Details rows */}
          <div className="scriptListRow scriptListRowTop">
            <div className="scriptListRowLabel">Script format:</div>
            <div className="scriptListPill scriptListPillFormat">{scriptFormatLabel}</div>
          </div>

          <div className="scriptListRow">
            <div className="scriptListRowLabel">Auto-generate shotlist:</div>

            <div className="scriptListPillWrap">
              <div
                className={[
                  'scriptListPill',
                  autoGenerateIsOn ? 'scriptListPillOn' : 'scriptListPillOff',
                  isFreeScenesTier ? 'scriptListPillFreeScenes' : '',
                ].join(' ')}
              >
                {autoGenerateLabel}
              </div>

              {showProOverlay ? (
                <div
                  className="scriptListProOverlay"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="scriptListProOverlayHitArea">
                    <ProFlag
                      type="pro"
                      featureMsg="Upgrade this project to Pro"
                      scriptLevel={script && script.features ? script.features : 'basic'}
                      scriptId={script.id}
                      handleUpgrade={refreshScripts}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="scriptListDivider" />

        {/* Footer */}
        <div className="scriptListFooter">
          <div className="scriptListFooterLeft">
            <div className={ownerPillClassNames}>
              <Tooltip
                title={`You are a ${isOwner ? 'Owner' : 'Collaborator'} on this project`}
                placement="top"
                arrow
              >
                <span className="scriptListOwnerInner">
                  <span className="scriptListOwnerEmoji">
                    {isOwner ? '🏆' : <FontAwesomeIcon icon={faUserGroup} />}
                  </span>
                  <span>{script.level.charAt(0).toUpperCase() + script.level.slice(1)}</span>
                </span>
              </Tooltip>
            </div>

            <div className="scriptListBubbles">
              <Tooltip
                title={`You are a ${isOwner ? 'Owner' : 'Collaborator'} on this project`}
                placement="top"
                arrow
              >
                <CollaboratorBubbleList
                  collaborators={script.collaborators}
                  setShowCollabDropdown={setShowCollabDropdown}
                  level={script.level}
                />
              </Tooltip>
            </div>

            {script.level === 'owner' && (
              <div className="scriptListCollabDropdownWrap">
                {showCollabDropdown && (
                  <CollaboratorDropdown
                    script={script}
                    updateScript={updateScript}
                    setShow={setShowCollabDropdown}
                  />
                )}
              </div>
            )}
          </div>

          <div className="scriptListFooterRight">
            <Tooltip title="Delete Project" placement="top" arrow>
              <span>
                <Icon
                  icon="Trash"
                  color="Orange"
                  customClass="scriptListTrashIcon"
                  onClick={() => deleteScriptModal(script.id, script.script_name)}
                />
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScriptList;
